import React, { useEffect, useState } from "react";
import { Spinner, Row, Col, Card, Badge, Alert as BsAlert, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import SafetyDashboardService from "../../services/safety/SafetyDashboardService";
import KpiCard from "../../components/safety/KpiCard/KpiCard";
import ComplianceSemaforo from "../../components/safety/ComplianceSemaforo/ComplianceSemaforo";
import AgenziaSelector, { useSafetyAgenziaContext } from "../../components/safety/AgenziaSelector/AgenziaSelector";

/**
 * SafetyDashboardPage
 *
 * Dashboard di compliance del modulo Adempimenti Sicurezza.
 * In FASE 1 mostra KPI base (aziende, lavoratori) + placeholder per i
 * KPI delle FASI successive (attestati, visite mediche, documenti).
 */
function fmtDate(d) {
    if (!d) return "—";
    const s = String(d).substring(0, 10);
    const parts = s.split("-");
    if (parts.length !== 3) return s;
    const [y, m, day] = parts;
    return `${day}/${m}/${y}`;
}

export default function SafetyDashboardPage() {
    const navigate = useNavigate();
    const { idAgenzia, setIdAgenzia } = useSafetyAgenziaContext();
    const [loading, setLoading] = useState(true);
    const [kpi, setKpi] = useState(null);
    const [topAziende, setTopAziende] = useState([]);
    const [scadenze, setScadenze] = useState([]);
    const [errore, setErrore] = useState(null);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoading(true);
            setErrore(null);
            try {
                const kpiResp = await SafetyDashboardService.getKpi(idAgenzia);
                const kpiRes = kpiResp && typeof kpiResp.json === "function" ? await kpiResp.json() : kpiResp;
                const topResp = await SafetyDashboardService.getTopAziende(5, idAgenzia);
                const topRes = topResp && typeof topResp.json === "function" ? await topResp.json() : topResp;
                const scadResp = await SafetyDashboardService.getScadenze(20, idAgenzia);
                const scadRes = scadResp && typeof scadResp.json === "function" ? await scadResp.json() : scadResp;
                if (!mounted) return;
                setKpi(kpiRes);
                setTopAziende(Array.isArray(topRes) ? topRes : []);
                setScadenze(Array.isArray(scadRes) ? scadRes : []);
            } catch (err) {
                if (mounted) {
                    setErrore(err?.message || "Errore nel caricamento dei dati di compliance");
                }
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [idAgenzia]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                <Spinner animation="border" />
            </div>
        );
    }

    if (errore) {
        return (
            <div className="p-3">
                <BsAlert variant="danger">{errore}</BsAlert>
            </div>
        );
    }

    const indice = Number(kpi?.indice_conformita ?? 0);
    const indiceTone = indice >= 90 ? "success" : indice >= 70 ? "warning" : "danger";

    return (
        <div className="p-2 p-md-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                <div>
                    <h4 className="mb-1">
                        <i className="bi bi-shield-check me-2 text-success" />
                        Gestione Adempimenti Sicurezza
                    </h4>
                    <div className="text-muted small">
                        Dashboard compliance D.Lgs. 81/2008
                    </div>
                </div>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                    <AgenziaSelector idAgenzia={idAgenzia} onChange={setIdAgenzia} />
                    <div className="d-flex align-items-center gap-2">
                        <Badge bg="light" text="dark" className="border">
                            {kpi?.fase_modulo || "FASE_1"}
                        </Badge>
                        <Badge bg={indiceTone}>Indice conformità: {indice}%</Badge>
                    </div>
                </div>
            </div>

            {/* Semafori globali */}
            <Card className="border-0 shadow-sm mb-3">
                <Card.Body>
                    <div className="d-flex flex-wrap gap-4 align-items-center">
                        <div>
                            <div className="text-muted small mb-1">Stato globale</div>
                            <ComplianceSemaforo
                                colore={kpi?.semafori?.globale?.colore || "GRAY"}
                                size="lg"
                                showLegend
                            />
                        </div>
                        <div>
                            <div className="text-muted small mb-1">Attestati</div>
                            <ComplianceSemaforo colore={kpi?.semafori?.attestati?.colore || "GRAY"} showLegend />
                        </div>
                        <div>
                            <div className="text-muted small mb-1">Visite mediche</div>
                            <ComplianceSemaforo colore={kpi?.semafori?.visite?.colore || "GRAY"} showLegend />
                        </div>
                        <div>
                            <div className="text-muted small mb-1">Documenti</div>
                            <ComplianceSemaforo colore={kpi?.semafori?.documenti?.colore || "GRAY"} showLegend />
                        </div>
                        <div>
                            <div className="text-muted small mb-1">Nomine e figure</div>
                            <ComplianceSemaforo colore={kpi?.semafori?.nomine?.colore || "GRAY"} showLegend />
                        </div>
                        <div>
                            <div className="text-muted small mb-1">Valutazioni rischi</div>
                            <ComplianceSemaforo colore={kpi?.semafori?.valutazioni?.colore || "GRAY"} showLegend />
                        </div>
                        <div>
                            <div className="text-muted small mb-1">Titoli aziendali</div>
                            <ComplianceSemaforo colore={kpi?.semafori?.licenze?.colore || "GRAY"} showLegend />
                        </div>
                        <div>
                            <div className="text-muted small mb-1">Appalti / imprese</div>
                            <ComplianceSemaforo colore={kpi?.semafori?.appalti?.colore || "GRAY"} showLegend />
                        </div>
                        <div>
                            <div className="text-muted small mb-1">Infortuni / near miss</div>
                            <ComplianceSemaforo colore={kpi?.semafori?.infortuni?.colore || "GRAY"} showLegend />
                        </div>
                        <div>
                            <div className="text-muted small mb-1">Riunione art. 35</div>
                            <ComplianceSemaforo colore={kpi?.semafori?.riunioni?.colore || "GRAY"} showLegend />
                        </div>
                        <div>
                            <div className="text-muted small mb-1">Sopralluoghi / NC</div>
                            <ComplianceSemaforo colore={kpi?.semafori?.sopralluoghi?.colore || "GRAY"} showLegend />
                        </div>
                        <div>
                            <div className="text-muted small mb-1">DPI per mansione</div>
                            <ComplianceSemaforo colore={kpi?.semafori?.dpi?.colore || "GRAY"} showLegend />
                        </div>
                        <div>
                            <div className="text-muted small mb-1">Lavoratori particolari</div>
                            <ComplianceSemaforo colore={kpi?.semafori?.particolari?.colore || "GRAY"} showLegend />
                        </div>
                        <div>
                            <div className="text-muted small mb-1">Scadenzario</div>
                            <ComplianceSemaforo colore={kpi?.semafori?.scadenzario?.colore || "GRAY"} showLegend />
                        </div>
                        <div>
                            <div className="text-muted small mb-1">Area ispettiva</div>
                            <ComplianceSemaforo colore={kpi?.semafori?.ispettiva?.colore || "GRAY"} showLegend />
                        </div>
                        <div>
                            <div className="text-muted small mb-1">Dotazioni</div>
                            <ComplianceSemaforo colore={kpi?.semafori?.dotazioni?.colore || "GRAY"} showLegend />
                        </div>
                        <div>
                            <div className="text-muted small mb-1">Attrezzature</div>
                            <ComplianceSemaforo colore={kpi?.semafori?.attrezzature?.colore || "GRAY"} showLegend />
                        </div>
                    </div>
                </Card.Body>
            </Card>

            {/* KPI cards */}
            <Row className="g-3 mb-3">
                <Col xs={12} sm={6} lg={3}>
                    <KpiCard
                        icon="bi-building"
                        title="La mia azienda"
                        value={kpi?.aziende_totali ?? 0}
                        subtitle={`${kpi?.aziende_attive ?? 0} attive`}
                        tone="primary"
                        onClick={() => navigate("/safety/aziende")}
                    />
                </Col>
                <Col xs={12} sm={6} lg={3}>
                    <KpiCard
                        icon="bi-people"
                        title="Lavoratori"
                        value={kpi?.lavoratori_totali ?? 0}
                        subtitle={`${kpi?.lavoratori_attivi ?? 0} attivi`}
                        tone="primary"
                        onClick={() => navigate("/safety/lavoratori")}
                    />
                </Col>
                <Col xs={12} sm={6} lg={3}>
                    <KpiCard
                        icon="bi-award"
                        title="Attestati in scadenza"
                        value={kpi?.attestati_in_scadenza ?? 0}
                        subtitle={`${kpi?.attestati_scaduti ?? 0} scaduti · ${kpi?.attestati_validi ?? 0} validi`}
                        tone="warning"
                        onClick={() => navigate("/safety/lavoratori")}
                    />
                </Col>
                <Col xs={12} sm={6} lg={3}>
                    <KpiCard
                        icon="bi-heart-pulse"
                        title="Visite mediche in scadenza"
                        value={kpi?.visite_in_scadenza ?? 0}
                        subtitle={`${kpi?.visite_scadute ?? 0} scadute`}
                        tone="warning"
                    />
                </Col>
            </Row>

            <Row className="g-3 mb-4">
                <Col xs={12} sm={6} lg={4}>
                    <KpiCard
                        icon="bi-file-earmark-text"
                        title="Documenti mancanti"
                        value={kpi?.documenti_mancanti ?? 0}
                        tone="danger"
                    />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                    <KpiCard
                        icon="bi-file-earmark-arrow-up"
                        title="Documenti in scadenza"
                        value={kpi?.documenti_in_scadenza ?? 0}
                        tone="warning"
                    />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                    <KpiCard
                        icon="bi-file-earmark-excel"
                        title="Documenti scaduti"
                        value={kpi?.documenti_scaduti ?? 0}
                        tone="danger"
                    />
                </Col>
            </Row>

            <Row className="g-3 mb-4">
                <Col xs={12} sm={6} lg={4}>
                    <KpiCard
                        icon="bi-person-badge"
                        title="Figure obbligatorie mancanti"
                        value={kpi?.nomine_mancanti ?? 0}
                        tone="danger"
                        onClick={() => navigate("/safety/aziende?tab=nomine")}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                    <KpiCard
                        icon="bi-person-check"
                        title="Aggiornamenti figure in scadenza"
                        value={kpi?.nomine_in_scadenza ?? 0}
                        subtitle={`${kpi?.nomine_scadute ?? 0} scaduti`}
                        tone="warning"
                        onClick={() => navigate("/safety/aziende?tab=nomine")}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                    <KpiCard
                        icon="bi-clipboard-data"
                        title="Valutazioni mancanti"
                        value={kpi?.valutazioni_mancanti ?? 0}
                        tone="danger"
                        onClick={() => navigate("/safety/aziende?tab=valutazioni")}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                    <KpiCard
                        icon="bi-clipboard-check"
                        title="Revisioni valutazioni in scadenza"
                        value={kpi?.valutazioni_in_scadenza ?? 0}
                        subtitle={`${kpi?.valutazioni_scadute ?? 0} scadute`}
                        tone="warning"
                        onClick={() => navigate("/safety/aziende?tab=valutazioni")}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                    <KpiCard
                        icon="bi-award"
                        title="Titoli obbligatori mancanti"
                        value={kpi?.licenze_mancanti ?? 0}
                        tone="danger"
                        onClick={() => navigate("/safety/aziende?tab=licenze")}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                    <KpiCard
                        icon="bi-award-fill"
                        title="Titoli in scadenza"
                        value={kpi?.licenze_in_scadenza ?? 0}
                        subtitle={`${kpi?.licenze_scadute ?? 0} scadute`}
                        tone="warning"
                        onClick={() => navigate("/safety/aziende?tab=licenze")}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                    <KpiCard
                        icon="bi-briefcase"
                        title="Adempimenti appalti in scadenza"
                        value={kpi?.appalti_in_scadenza ?? 0}
                        subtitle={`${kpi?.appalti_scaduti ?? 0} scaduti · ${kpi?.appalti_totali ?? 0} totali`}
                        tone="warning"
                        onClick={() => navigate("/safety/aziende?tab=appalti")}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                    <KpiCard
                        icon="bi-bandaid"
                        title="Infortuni anno corrente"
                        value={kpi?.infortuni_anno ?? 0}
                        subtitle={`${kpi?.near_miss_anno ?? 0} near miss · ${kpi?.infortuni_gravi ?? 0} gravi`}
                        tone={(kpi?.infortuni_gravi ?? 0) > 0 ? "danger" : "warning"}
                        onClick={() => navigate("/safety/aziende?tab=infortuni")}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                    <KpiCard
                        icon="bi-file-earmark-medical"
                        title="Denunce INAIL mancanti"
                        value={kpi?.infortuni_denunce_mancanti ?? 0}
                        subtitle={`${kpi?.infortuni_giorni_assenza ?? 0} giorni assenza`}
                        tone="danger"
                        onClick={() => navigate("/safety/aziende?tab=infortuni")}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                    <KpiCard
                        icon="bi-calendar-check"
                        title="Riunioni periodiche mancanti"
                        value={kpi?.riunioni_mancanti ?? 0}
                        subtitle={`${kpi?.riunioni_svolte_anno ?? 0}/${kpi?.riunioni_aziende_obbligate ?? 0} svolte · ${kpi?.riunioni_verbali_mancanti ?? 0} senza verbale`}
                        tone={(kpi?.riunioni_mancanti ?? 0) > 0 ? "danger" : "success"}
                        onClick={() => navigate("/safety/aziende?tab=riunioni")}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                    <KpiCard
                        icon="bi-exclamation-triangle"
                        title="Non conformità aperte"
                        value={kpi?.nc_aperte ?? 0}
                        subtitle={`${kpi?.nc_scadute ?? 0} scadute · ${kpi?.nc_critiche_aperte ?? 0} critiche`}
                        tone={(kpi?.nc_scadute ?? 0) > 0 ? "danger" : "warning"}
                        onClick={() => navigate("/safety/aziende?tab=sopralluoghi")}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                    <KpiCard
                        icon="bi-search"
                        title="Sopralluoghi anno corrente"
                        value={kpi?.sopralluoghi_anno ?? 0}
                        subtitle={`${kpi?.nc_in_scadenza ?? 0} NC in scadenza`}
                        tone="primary"
                        onClick={() => navigate("/safety/aziende?tab=sopralluoghi")}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                    <KpiCard
                        icon="bi-shield"
                        title="Lavoratori senza DPI"
                        value={kpi?.dpi_lavoratori_non_conformi ?? 0}
                        subtitle={`${kpi?.dpi_consegne_anno ?? 0} consegne anno · ${kpi?.dpi_consegne_scadute ?? 0} scaduti`}
                        tone={(kpi?.dpi_lavoratori_non_conformi ?? 0) > 0 ? "danger" : "success"}
                        onClick={() => navigate("/safety/aziende?tab=dpi")}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                    <KpiCard
                        icon="bi-folder-symlink"
                        title="Prescrizioni ispettive scadute"
                        value={kpi?.ispettiva_prescrizioni_scadute ?? 0}
                        subtitle={`${kpi?.ispettiva_prescrizioni_aperte ?? 0} aperte · ${kpi?.ispettiva_non_conformi ?? 0} NC checklist`}
                        tone={(kpi?.ispettiva_prescrizioni_scadute ?? 0) > 0 ? "danger" : "warning"}
                        onClick={() => navigate("/safety/aziende?tab=ispettiva")}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                    <KpiCard
                        icon="bi-bell"
                        title="Scadenze urgenti"
                        value={kpi?.scadenzario_scadute ?? 0}
                        subtitle={`${kpi?.scadenzario_in_scadenza ?? 0} in scadenza · ${kpi?.scadenzario_totale ?? 0} totali`}
                        tone={(kpi?.scadenzario_scadute ?? 0) > 0 ? "danger" : "warning"}
                        onClick={() => navigate("/safety/scadenzario")}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                    <KpiCard
                        icon="bi-person-hearts"
                        title="Particolari senza valutazione"
                        value={kpi?.particolari_valutazioni_mancanti ?? 0}
                        subtitle={`${kpi?.particolari_attivi ?? 0} attivi · ${kpi?.particolari_valutazioni_scadute ?? 0} revisioni scadute`}
                        tone={(kpi?.particolari_valutazioni_mancanti ?? 0) > 0 ? "danger" : "success"}
                        onClick={() => navigate("/safety/aziende?tab=particolari")}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                    <KpiCard
                        icon="bi-shield-shaded"
                        title="Dotazioni in scadenza"
                        value={kpi?.dotazioni_in_scadenza ?? 0}
                        subtitle={`${kpi?.dotazioni_scadute ?? 0} scadute · ${kpi?.dotazioni_totali ?? 0} totali`}
                        tone="warning"
                        onClick={() => navigate("/safety/aziende?tab=dotazioni")}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                    <KpiCard
                        icon="bi-tools"
                        title="Attrezzature / impianti"
                        value={kpi?.attrezzature_totali ?? 0}
                        subtitle="soggette a verifica"
                        tone="primary"
                        onClick={() => navigate("/safety/aziende?tab=attrezzature")}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                    <KpiCard
                        icon="bi-clipboard-check"
                        title="Verifiche in scadenza"
                        value={kpi?.verifiche_in_scadenza ?? 0}
                        tone="warning"
                        onClick={() => navigate("/safety/aziende")}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                    <KpiCard
                        icon="bi-clipboard-x"
                        title="Verifiche scadute"
                        value={kpi?.verifiche_scadute ?? 0}
                        tone="danger"
                        onClick={() => navigate("/safety/aziende")}
                    />
                </Col>
            </Row>

            {/* Prossime scadenze formative (dati reali attestati) */}
            <Card className="border-0 shadow-sm mb-3">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">
                            <i className="bi bi-alarm me-2 text-warning" />
                            Prossime scadenze formative
                        </h6>
                        <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => navigate("/safety/scadenzario")}
                        >
                            Scadenzario completo
                        </button>
                    </div>

                    {scadenze.length === 0 ? (
                        <div className="text-muted py-3 text-center">
                            <i className="bi bi-check2-circle me-1 text-success" />
                            Nessun attestato scaduto o in scadenza nei prossimi 60 giorni.
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table hover size="sm" className="mb-0 align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th>Lavoratore</th>
                                        <th>Azienda</th>
                                        <th>Attestato</th>
                                        <th>Scadenza</th>
                                        <th>Stato</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {scadenze.map(s => {
                                        const scaduto = s.stato === "SCADUTO";
                                        const g = Number(s.giorni);
                                        return (
                                            <tr key={s.id_safety_attestato}>
                                                <td className="fw-semibold">{s.cognome} {s.nome}</td>
                                                <td>{s.ragione_sociale || <span className="text-muted">—</span>}</td>
                                                <td>{s.titolo || s.tipo_denominazione || <span className="text-muted">—</span>}</td>
                                                <td>{fmtDate(s.data_scadenza)}</td>
                                                <td>
                                                    <Badge bg={scaduto ? "danger" : "warning"} text={scaduto ? "light" : "dark"}>
                                                        {scaduto ? "Scaduto" : "In scadenza"}
                                                    </Badge>
                                                    <span className="text-muted small ms-1">
                                                        ({g < 0 ? `${Math.abs(g)} gg fa` : `tra ${g} gg`})
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Avviso domini attivi */}
            <BsAlert variant="success" className="small">
                <i className="bi bi-check2-circle me-2" />
                <strong>Compliance D.Lgs. 81/08 completa.</strong> Sono operativi tutti i domini:
                anagrafiche (aziende, sedi, lavoratori, mansioni), <em>formazione</em> (attestati e
                scadenze, sync eduform + manuale), <em>sorveglianza sanitaria</em> (visite mediche art. 41)
                <em>archivio documentale</em> (DVR, nomine, DPI…) con rilevazione dei documenti
                obbligatori mancanti, <em>autorizzazioni, polizze e certificazioni aziendali</em>,
                <em>abilitazioni del personale</em>
                (GPG, porto d'armi, tessere) e <em>attrezzature/impianti</em> con verifiche periodiche
                (art. 71, DPR 462, antincendio).
            </BsAlert>

            {/* Top aziende */}
            <Card className="border-0 shadow-sm">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">Top aziende per numero di lavoratori</h6>
                        <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => navigate("/safety/aziende")}
                        >
                            Tutte le aziende
                        </button>
                    </div>

                    {topAziende.length === 0 ? (
                        <div className="text-muted py-3 text-center">
                            Nessuna azienda registrata. Inizia da{" "}
                            <span
                                role="button"
                                className="text-primary text-decoration-underline"
                                onClick={() => navigate("/safety/aziende")}
                            >
                                La mia azienda
                            </span>.
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table hover className="mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th>Azienda</th>
                                        <th>Classe rischio</th>
                                        <th className="text-end">Lavoratori</th>
                                        <th className="text-end">Azioni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {topAziende.map(a => (
                                        <tr key={a.id_azienda}>
                                            <td>{a.ragione_sociale}</td>
                                            <td>
                                                {a.classe_rischio
                                                    ? <Badge bg={a.classe_rischio === "ALTO" ? "danger" : a.classe_rischio === "MEDIO" ? "warning" : "success"}>{a.classe_rischio}</Badge>
                                                    : <span className="text-muted">—</span>}
                                            </td>
                                            <td className="text-end">{a.num_lavoratori}</td>
                                            <td className="text-end">
                                                <button
                                                    className="btn btn-sm btn-link p-0"
                                                    onClick={() => navigate(`/safety/aziende?id=${a.id_azienda}`)}
                                                >
                                                    Apri
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
}
